import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './TaskPreview.css';
import { useNavigate } from "./Router";
import { FaMapMarkerAlt } from 'react-icons/fa';

/**
 * Composant TaskPreview
 * Affiche un résumé de tâche lorsqu'une publication est associée à une tâche.
 * Permet de naviguer vers la page détaillée de la tâche en cliquant sur le résumé.
 */
const TaskPreview = ({ taskId }) => {
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTaskData = async () => {
      if (!taskId) {
        setLoading(false);
        return;
      }

      try {
        // Récupérer les données de la tâche
        const taskData = await sdk.models.Task.getTask({id: taskId});
        setTask(taskData);

        // Récupérer le modèle de la tâche
        if (taskData && taskData.modelId) {
          const modelData = await sdk.models.TaskModel.getOne(taskData.modelId);
          setModel(modelData);
        }

        setLoading(false);
      } catch (err) {
        console.error("Erreur lors du chargement de la tâche:", err);
        setError("Impossible de charger les détails de la tâche");
        setLoading(false);
      }
    };

    fetchTaskData();
  }, [taskId]);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/task/${taskId}`);
  };

  // Formater le statut de la tâche
  const getStatusInfo = () => {
    if (!task) return { label: "Inconnu", className: "tp-status-unknown" };

    switch (task.status) {
      case 0:
        return { label: "Disponible", className: "tp-status-available" };
      case 1:
        return { label: "En cours", className: "tp-status-progress" };
      case 2:
        return { label: "Terminée", className: "tp-status-completed" };
      case -1:
        return { label: "Annulée", className: "tp-status-cancelled" };
      default:
        return { label: "Inconnu", className: "tp-status-unknown" };
    }
  };

  // Extraire la description des détails
  const getTaskDescription = () => {
    if (!task || !task.details) return "";
    
    try {
      const details = typeof task.details === 'string' 
        ? JSON.parse(task.details) 
        : task.details;
      
      return details.description || details.details || "";
    } catch (e) {
      console.error("Erreur lors de l'analyse des détails:", e);
      return "";
    }
  };

  // Extraire le montant de la récompense
  const getRewardInfo = () => {
    if (!task || !task.reward) return "À définir";
    
    try {
      const reward = typeof task.reward === 'string' 
        ? JSON.parse(task.reward) 
        : task.reward;
      
      if (reward.amount) {
        return `${reward.amount} ${reward.crypto || 'CAD'}`;
      } else {
        return "À négocier";
      }
    } catch (e) {
      console.error("Erreur lors de l'analyse de la récompense:", e);
      return "À définir";
    }
  };

  // Récupérer la ville de l'adresse de la tâche
  const getTaskLocation = () => {
    if (!task || !task.address) return null;
    
    const address = typeof task.address === 'string'
      ? JSON.parse(task.address)
      : task.address;
    
    return address.city || null;
  };

  if (loading) {
    return (
      <div className="tp-container tp-loading">
        <div className="tp-loading-indicator"></div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="tp-container tp-error">
        <p>{error || "Cette tâche n'existe plus ou n'est pas disponible"}</p>
      </div>
    );
  }

  const statusInfo = getStatusInfo();
  const description = getTaskDescription();
  const truncatedDescription = description.length > 120 
    ? `${description.substring(0, 120)}...` 
    : description;
  const location = getTaskLocation();

  return (
    <div className="tp-container" onClick={handleClick}>
      <div className="tp-header">
        <div className="tp-icon-container">
          {model && model.icon ? (
            <img 
              src={model.icon} 
              alt={model?.name || "Tâche"} 
              className="tp-icon" 
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://api.liberium.net/s/liberium-public-img/peuple_qc_ico_transparent.png";
              }}
            />
          ) : (
            <div className="tp-icon-placeholder">
              <i className="fas fa-briefcase"></i>
            </div>
          )}
        </div>
        <div className="tp-title-container">
          <h3 className="tp-title">{model?.name || "Tâche"}</h3>
          <div className="tp-meta">
            <span className={`tp-status ${statusInfo.className}`}>
              {statusInfo.label}
            </span>
            {location && (
              <span className="tp-location">
                <FaMapMarkerAlt style={{ marginRight: '4px', fontSize: '12px' }} />
                {location}
              </span>
            )}
            <span className="tp-reward">
              {getRewardInfo()}
            </span>
          </div>
        </div>
      </div>
      
      {truncatedDescription && (
        <div className="tp-body">
          <p className="tp-description">{truncatedDescription}</p>
        </div>
      )}
      
      <div className="tp-footer">
        <span className="tp-view-task">
          Voir les détails <i className="fas fa-chevron-right"></i>
        </span>
      </div>
    </div>
  );
};

export { TaskPreview };
export default TaskPreview;